/**
 * @file CameraScanButton.tsx
 * @description Кнопка «Сканировать камерой» 48px у поля скана и её диалог
 *   (phase19.22 шаг 5).
 *
 * Видна только там, где камера реально поможет (`useCameraAvailable`): на
 * телефоне/планшете с камерой и по HTTPS. На десктопе с USB-сканером её нет —
 * там сканер печатает прямо в поле.
 *
 * Код с камеры уходит в `onSubmit` с `fromScanner: true` — для экрана это
 * такой же скан, как с USB: звук успеха/ошибки проигрывает экран по итогу
 * запроса, а здесь — короткое вибро «поймал код» (если обратная связь
 * включена в настройках рабочего места).
 *
 * phase19.31 (зона «Склад»): компактная кнопка-значок внутри ряда поля —
 * размер из темы (`--control-compact-h`: 32 с 1024, 48 до 1024), а не своя
 * крупная плитка. `density` оставлен для совместимости и вид не меняет.
 */

import { Camera } from "lucide-react";
import { useState } from "react";

import { CameraScanDialog } from "./CameraScanDialog";
import type { CameraMode } from "./camera-scan";
import { markNoCamera, useCameraAvailable } from "./useCameraAvailable";
import { isScanFeedbackEnabled } from "@/lib/scan-feedback";

interface Props {
  mode: CameraMode;
  disabled?: boolean;
  onDetected: (code: string) => void;
  /** @deprecated Размер даёт тема; значение не влияет на вид. */
  density?: "wms" | "standard";
}

export function CameraScanButton({ mode, disabled = false, onDetected }: Props) {
  const available = useCameraAvailable(mode);
  const [open, setOpen] = useState(false);
  if (!available) return null;

  function detected(code: string) {
    setOpen(false);
    if (isScanFeedbackEnabled()) navigator.vibrate?.(50);
    onDetected(code);
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        aria-label="Сканировать камерой"
        title="Сканировать камерой"
        className="inline-flex size-[var(--control-compact-h,3rem)] shrink-0 items-center justify-center rounded-md text-on-surface-variant hover:bg-surface-low hover:text-primary disabled:opacity-50"
      >
        <Camera className="size-4" />
      </button>
      {open ? (
        <CameraScanDialog
          open={open}
          onClose={({ noCamera }) => {
            setOpen(false);
            // Прячем кнопку только после закрытия: пока диалог открыт, в нём текст ошибки.
            if (noCamera) markNoCamera();
          }}
          onDetected={detected}
        />
      ) : null}
    </>
  );
}
